<script setup lang="ts">
import { Spinner } from "@/components/ui/spinner";
import { ChevronRightIcon, TriangleAlertIcon } from "lucide-vue-next";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

import {
  serviceKilledState,
  dismissServiceKilledBanner,
  openServiceKilledRemediation,
} from "../capacitor/service-health";

const { t } = useI18n();

const busy = ref(false);

const visible = computed(() => serviceKilledState.value !== null);

// Call t() directly with template literals so the i18n static scanner
// registers `msg.serviceKilled.oem.` as a dynamic prefix and does not flag
// the per-OEM keys as unused.
const body = computed(() => {
  if (serviceKilledState.value?.batteryOptExempt) {
    return t("msg.serviceKilled.dialogBodyWithExemption");
  }
  return t("msg.serviceKilled.dialogBody");
});

const oemTitle = computed(() => {
  const kind = serviceKilledState.value?.kind;
  if (!kind) return "";
  return t(`msg.serviceKilled.oem.${kind}.title`);
});

const oemSteps = computed<string[]>(() => {
  const kind = serviceKilledState.value?.kind;
  if (!kind) return [];
  return [
    t(`msg.serviceKilled.oem.${kind}.step1`),
    t(`msg.serviceKilled.oem.${kind}.step2`),
    t(`msg.serviceKilled.oem.${kind}.step3`),
  ];
});

async function handleOpen(): Promise<void> {
  if (busy.value) return;
  busy.value = true;
  try {
    await openServiceKilledRemediation();
  } finally {
    busy.value = false;
  }
}

function handleDismiss(): void {
  dismissServiceKilledBanner();
}
</script>

<template>
  <Transition name="service-killed-fade">
    <div
      v-if="visible"
      class="fixed inset-x-4 bottom-4 z-90 mx-auto max-w-md rounded-lg border border-amber-500/40 bg-background p-4 shadow-lg sm:bottom-6"
      role="alert"
      aria-live="polite"
    >
      <div class="flex items-start gap-3">
        <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-500/15">
          <TriangleAlertIcon class="size-4 text-amber-600 dark:text-amber-500" aria-hidden="true" />
        </div>
        <div class="flex-1">
          <h2 class="text-sm font-semibold text-foreground">
            {{ t("msg.serviceKilled.bannerTitle") }}
          </h2>
          <p class="mt-1 text-xs text-muted-foreground">
            {{ body }}
          </p>
          <Collapsible v-slot="{ open }" class="mt-2">
            <CollapsibleTrigger
              class="flex items-center gap-1 text-left text-xs font-medium text-amber-600 dark:text-amber-500"
            >
              <ChevronRightIcon
                class="size-3 shrink-0 transition-transform"
                :class="{ 'rotate-90': open }"
                aria-hidden="true"
              />
              {{ oemTitle }}
            </CollapsibleTrigger>
            <CollapsibleContent>
              <ol class="mt-2 list-inside list-decimal space-y-1 text-xs text-muted-foreground">
                <li v-for="(step, i) in oemSteps" :key="i">{{ step }}</li>
              </ol>
            </CollapsibleContent>
          </Collapsible>
          <div class="mt-3 flex flex-wrap gap-2">
            <button
              @click="handleOpen"
              :disabled="busy"
              class="rounded-md bg-amber-500 px-3 py-1.5 text-xs font-semibold text-gray-950 hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Spinner v-if="busy" class="mr-1" />
              {{ t("msg.serviceKilled.openSettings") }}
            </button>
            <button
              @click="handleDismiss"
              class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted"
            >
              {{ t("msg.serviceKilled.dismiss") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.service-killed-fade-enter-active,
.service-killed-fade-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}
.service-killed-fade-enter-from,
.service-killed-fade-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
</style>
